import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

/**
 * Chapter 11: Collective Consciousness
 * 12+ nodes distributed on sphere surface (Fibonacci sphere)
 * Dynamic connection lines between nearby nodes
 * Connections pulse with audio
 */
export default function CollectiveNetwork({ audioData, isActive, position = [0, 0, 0] }) {
  const nodesRef = useRef([])
  const linesRef = useRef([])
  const groupRef = useRef()

  const { fft, bass, mids, highs, amplitude, beat } = audioData || {
    fft: new Float32Array(128).fill(0),
    bass: 0,
    mids: 0,
    highs: 0,
    amplitude: 0,
    beat: 0
  }

  const nodeCount = 16

  // Fibonacci sphere distribution for even node placement
  const nodePositions = useMemo(() => {
    const positions = []
    const phi = Math.PI * (3 - Math.sqrt(5)) // Golden angle

    for (let i = 0; i < nodeCount; i++) {
      const y = 1 - (i / (nodeCount - 1)) * 2
      const radius = Math.sqrt(1 - y * y)
      const theta = phi * i

      const x = Math.cos(theta) * radius
      const z = Math.sin(theta) * radius

      positions.push(new THREE.Vector3(x * 3, y * 3, z * 3))
    }

    return positions
  }, [])

  // Calculate connections between nearby nodes
  const connections = useMemo(() => {
    const conns = []
    const maxDistance = 2.5

    for (let i = 0; i < nodePositions.length; i++) {
      for (let j = i + 1; j < nodePositions.length; j++) {
        const distance = nodePositions[i].distanceTo(nodePositions[j])
        if (distance < maxDistance) {
          conns.push({ from: i, to: j, distance })
        }
      }
    }

    return conns
  }, [nodePositions])

  // Pre-create line geometries ONCE (not per frame)
  const lineGeometries = useMemo(() => {
    return connections.map((conn) => {
      const from = nodePositions[conn.from]
      const to = nodePositions[conn.to]
      const geometry = new THREE.BufferGeometry().setFromPoints([from, to])
      return geometry
    })
  }, [connections, nodePositions])

  useFrame((state, delta) => {
    if (!isActive) return

    const time = state.clock.elapsedTime

    // Rotate entire network
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.15
      groupRef.current.rotation.x = Math.sin(time * 0.3) * 0.2
    }

    // Animate nodes
    nodesRef.current.forEach((node, i) => {
      if (!node) return

      node.rotation.y += delta * (0.15 + i * 0.03)
      node.rotation.x += delta * (0.1 + i * 0.02)

      const basePulse = 1 + Math.sin(time * 0.8 + i * 0.5) * 0.1
      const audioPulse = amplitude * 0.3
      node.scale.setScalar(beat > 0.5 ? 1.5 : basePulse + audioPulse)

      const fftIndex = Math.floor((i / nodeCount) * fft.length)
      const fftValue = fft[fftIndex] || 0
      if (node.material) {
        node.material.emissiveIntensity = 1 + fftValue * 2
      }
    })

    // Animate connection lines
    linesRef.current.forEach((line, i) => {
      if (!line || !line.material) return

      const pulsePhase = (time * 0.8 + i * 0.3) % (Math.PI * 2)
      const basePulse = Math.sin(pulsePhase) * 0.3 + 0.5
      line.material.opacity = basePulse + amplitude * 0.3

      const fftIndex = Math.floor((i / connections.length) * fft.length)
      const fftValue = fft[fftIndex] || 0
      line.material.color.setRGB(0, 0.5 + fftValue, 1)
    })
  })

  return (
    <group position={position} ref={groupRef}>
      {/* Nodes - reduced to single light instead of per-node */}
      {nodePositions.map((pos, i) => (
        <mesh
          key={`node-${i}`}
          position={[pos.x, pos.y, pos.z]}
          ref={(el) => (nodesRef.current[i] = el)}
        >
          <octahedronGeometry args={[0.2, 0]} />
          <meshStandardMaterial
            color="#00FFFF"
            emissive="#00FFFF"
            emissiveIntensity={1}
            transparent
            opacity={0.9}
            metalness={0.8}
            roughness={0.2}
          />
        </mesh>
      ))}

      {/* Connection lines - using pre-created geometries */}
      {lineGeometries.map((geom, i) => (
        <line
          key={`connection-${i}`}
          geometry={geom}
          ref={(el) => (linesRef.current[i] = el)}
        >
          <lineBasicMaterial
            color="#00FFFF"
            transparent
            opacity={0.5}
            linewidth={2}
            blending={THREE.AdditiveBlending}
          />
        </line>
      ))}

      {/* Single central ambient light instead of 16 per-node lights */}
      <pointLight position={[0, 0, 0]} intensity={3} color="#00FFFF" distance={10} />

      {/* Outer glow sphere */}
      <mesh>
        <sphereGeometry args={[3.5, 32, 32]} />
        <meshBasicMaterial
          color="#0088FF"
          transparent
          opacity={0.05}
          side={THREE.BackSide}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </group>
  )
}
